<template>
  <v-dialog :model-value="visible" max-width="520" persistent @update:modelValue="close">
    <v-card class="quota-dialog">
      <div class="quota-dialog__header" :class="`quota-dialog__header--${variant}`">
        <div class="quota-dialog__icon">
          <component :is="headerIcon" :size="28" />
        </div>
        <h2 class="quota-dialog__title">{{ title }}</h2>
      </div>

      <div class="quota-dialog__body">
        <p class="quota-dialog__message">{{ message }}</p>

        <div v-if="quota" class="quota-dialog__stats">
          <div class="quota-stat">
            <span class="quota-stat__label">{{ $gt('Standorte') }}</span>
            <span class="quota-stat__value">
              <strong>{{ quota.usedLocations }}</strong> / {{ quota.licensedLocations }}
            </span>
          </div>
          <div class="quota-stat">
            <span class="quota-stat__label">{{ $gt('Mitarbeiter') }}</span>
            <span class="quota-stat__value">
              <strong>{{ quota.usedUsers }}</strong> / {{ quota.licensedUsers }}
            </span>
          </div>
          <div class="quota-stat">
            <span class="quota-stat__label">{{ $gt('Aktuelles Paket') }}</span>
            <span class="quota-stat__value">
              <span class="quota-plan-badge">{{ planLabel(quota.plan) }}</span>
            </span>
          </div>
        </div>

        <p v-if="canUpgrade" class="quota-dialog__hint">
          {{ $gt('Erweitern Sie Ihr Paket, um weitere') }} {{ resourceLabel }} {{ $gt('anzulegen. Sie werden anschließend zu unserem Zahlungs-Portal weitergeleitet.') }}
        </p>
        <p v-else class="quota-dialog__hint">
          {{ $gt('Bitte wenden Sie sich an Ihren Administrator, um das Paket zu erweitern.') }}
        </p>
      </div>

      <div class="quota-dialog__actions">
        <v-btn variant="text" color="grey-darken-1" @click="close">{{ $gt('Später') }}</v-btn>
        <v-btn v-if="canUpgrade" color="primary" variant="flat" :loading="upgrading" @click="upgrade">
          <ZapIcon :size="16" class="mr-1" />
          {{ $gt('Jetzt erweitern') }}
        </v-btn>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import { AlertTriangle, Users, MapPinned, Zap as ZapIcon } from "lucide-vue-next";
import { createUpgradeSession } from "@/services/billingApi";
import { useSnackbarStore } from "@/stores/snackbarStore";
import { useAuthStore } from "@/stores/authStore";

export default {
  name: "LicenseQuotaDialog",

  components: { ZapIcon },

  props: {
    visible: { type: Boolean, default: false },
    quota: { type: Object, default: null },
  },

  emits: ["close"],

  data() {
    return { upgrading: false };
  },

  computed: {
    canUpgrade() {
      return useAuthStore().isAdministrator;
    },
    variant() {
      return this.quota?.code === "license_location_quota_exceeded" ? "locations" : "users";
    },
    headerIcon() {
      if (this.variant === "locations") return MapPinned;
      if (this.variant === "users") return Users;
      return AlertTriangle;
    },
    title() {
      return this.variant === "locations" ? "Standort-Kontingent aufgebraucht" : "Lizenz-Kontingent erreicht";
    },
    message() {
      return (
        this.quota?.message ||
        "Ihr gebuchtes Paket erlaubt das Anlegen weiterer Einträge nicht."
      );
    },
    resourceLabel() {
      return this.variant === "locations" ? "Standorte" : "Mitarbeiter";
    },
  },

  methods: {
    planLabel(plan) {
      switch (plan) {
        case "solo":
          return "Solo";
        case "basis":
          return "Basis";
        case "enterprise":
          return "Enterprise";
        default:
          return plan || "–";
      }
    },
    close() {
      if (this.upgrading) return;
      this.$emit("close");
    },
    async upgrade() {
      this.upgrading = true;
      try {
        const { url } = await createUpgradeSession();
        if (url) {
          window.location.href = url;
          return;
        }
      } catch (e) {
        useSnackbarStore().showSnackbar({
          message: e?.message || "Upgrade konnte nicht gestartet werden.",
          type: "error",
        });
      } finally {
        this.upgrading = false;
      }
    },
  },
};
</script>

<style scoped>
.quota-dialog {
  border-radius: 14px;
  overflow: hidden;
}
.quota-dialog__header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 22px 24px 18px;
  color: #fff;
}
.quota-dialog__header--users {
  background: linear-gradient(135deg, #1d4ed8 0%, #2a63d8 100%);
}
.quota-dialog__header--locations {
  background: linear-gradient(135deg, #1d4ed8 0%, #2a63d8 100%);
}
.quota-dialog__icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(6px);
  flex-shrink: 0;
}
.quota-dialog__title {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 600;
  letter-spacing: -0.01em;
}
.quota-dialog__body {
  padding: 22px 24px 4px;
}
.quota-dialog__message {
  margin: 0 0 18px;
  font-size: 0.94rem;
  color: var(--dark-color);
  line-height: 1.55;
}
.quota-dialog__stats {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 18px;
}
.quota-stat {
  background: var(--grey-light-color);
  border-radius: 10px;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.quota-stat__label {
  font-size: 0.7rem;
  color: var(--grey-color);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-weight: 600;
}
.quota-stat__value {
  font-size: 0.95rem;
  color: var(--dark-color);
}
.quota-stat__value strong {
  font-weight: 700;
  color: #991b1b;
}
.quota-plan-badge {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 999px;
  background: var(--primary-color);
  color: #fff;
  font-size: 0.75rem;
  font-weight: 600;
}
.quota-dialog__hint {
  margin: 0;
  font-size: 0.82rem;
  color: var(--grey-color);
  line-height: 1.5;
  background: #eef4ff;
  padding: 12px 14px;
  border-radius: 10px;
  border: 1px solid #dbeafe;
}
.quota-dialog__actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 16px 24px 20px;
}
</style>
